import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { CustomerController } from "../controllers/customer";
import { identify } from "../lib/analytics";
import { config } from "../config";
import { getAccessToken, getIdTokenClaims, beginLogin, logout as oidcLogout } from "../auth/oidc";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        if (config.isEmbedded) {
          // NetSuite session cookie — Suitelet resolves the user.
          const s = await CustomerController.session();
          if (!cancelled) { setUser(s?.user || null); identify(s?.user); }
          return;
        }
        // Hosted: rely on OIDC token + BFF-resolved profile.
        const tok = getAccessToken();
        if (!tok) { if (!cancelled) setUser(null); return; }
        const claims = getIdTokenClaims();
        // Try the BFF for the canonical profile (company, role, etc.); fall
        // back to id_token claims if the call fails.
        try {
          const s = await CustomerController.session();
          if (!cancelled) { setUser(s?.user || null); identify(s?.user); }
        } catch {
          if (claims && !cancelled) {
            const u = {
              id: claims.sub,
              email: claims.email,
              firstName: claims.given_name,
              lastName: claims.family_name,
              company: claims.company || claims.organization,
              priceLevel: claims.price_level || claims.priceLevel || "base",
            };
            setUser(u); identify(u);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    bootstrap();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (returnTo) => {
    setError(null);
    if (config.isEmbedded) {
      throw new Error("embedded mode uses NetSuite SSO");
    }
    await beginLogin(returnTo || window.location.pathname);
  }, []);

  const logout = useCallback(async () => {
    if (config.isEmbedded) {
      await CustomerController.logout().catch(() => null);
      setUser(null);
      return;
    }
    setUser(null);
    await oidcLogout();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
